/**
 * Organization Approval Confirmed email.
 *
 * Sent when a super admin approves the org owner from
 * /organizations. Replaces the older, longer onboarding +
 * underwriting variant with the cleaner design that surfaces the
 * approved user, a single CTA to finalize account setup, and the
 * two remaining next-steps (team integration + financial config).
 *
 * Layout uses the shared header (isBroker:true → sky background +
 * white TrustJet logo) and the shared footer (download-app row +
 * dark bar with socials).
 */

import { spacer, emailDocOpen, emailDocClose, emailHeader, emailFooter, icon } from '../emailLayout';

// Where the "Finalize Account Setup" button lands. Points at the
// live app root — which is the sign-in landing page — so the
// approved owner can log in and pick up the rest of their setup.
// Env-aware, mirrors the baseUrl pattern in emailLayout.js.
const LOGIN_URL = process.env.REACT_APP_ENVIRONMENT === 'PROD'
  ? 'https://app.trustjet.ai'
  : 'https://dev.trustjet.ai';

/**
 * Build the organization approval confirmation email HTML.
 *
 * @param {Object} params
 * @param {string}  params.firstName        - Approved user's first name
 * @param {string} [params.lastName]        - Approved user's last name (both name parts render in the User card)
 * @param {string}  params.email            - Email to show in the "Signed up using" cell
 * @param {Object} [params.organization]    - Passed to the shared header
 * @returns {string} HTML email body
 */
export const buildOrgApprovalEmailHtml = ({
  firstName,
  lastName = '',
  email,
  organization,
}) => {
  const fullName = [firstName, lastName].filter(Boolean).join(' ') || firstName || 'there';
  const contactEmail = email || '';
  return `${emailDocOpen({ title: 'Organization Approval Confirmed' })}

${emailHeader({
  title: 'Organization Approval Confirmed',
  subtitle: 'Account Activation Complete',
  organization,
  isBroker: true,
})}

${spacer(16)}

<!-- ===== USER / EMAIL CARD ===== -->
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;border:1px solid #EBEBEB;">
  <tr>
    <td style="padding:24px 32px;">
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0">
        <tr>
          <td width="50%" style="vertical-align:top;">
            <p style="margin:0 0 8px;font-size:11px;text-transform:uppercase;color:#6B7280;letter-spacing:0.8px;font-weight:500;">User</p>
            <p style="margin:0;font-size:15px;color:#111827;font-weight:600;">${fullName}</p>
          </td>
          <td width="50%" style="vertical-align:top;text-align:right;">
            <p style="margin:0 0 8px;font-size:11px;text-transform:uppercase;color:#6B7280;letter-spacing:0.8px;font-weight:500;">Signed up using</p>
            <p style="margin:0;font-size:15px;color:#111827;font-weight:600;word-break:break-all;">${contactEmail}</p>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>

${spacer(16)}

<!-- ===== MAIN CONTENT CARD ===== -->
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;background-color:#FFFFFF;border:1px solid #EBEBEB;">
  <!-- Icon: envelope in green circle. Matches the pattern used by
       the Itinerary Proposal email — colored <div> for the circle,
       icon() helper for the glyph (hosted SVG via <img src="…">).
       Inline data:image/svg+xml URIs get stripped by Gmail webmail
       and some Outlook builds, which was rendering as a broken
       image in the recipient's inbox. -->
  <tr>
    <td style="padding:32px 32px 0;">
      <table role="presentation" cellpadding="0" cellspacing="0">
        <tr>
          <td width="56" valign="top">
            <div style="width:48px;height:48px;background-color:#DCFCE7;border-radius:50%;text-align:center;line-height:48px;">
              ${icon('mail', 24)}
            </div>
          </td>
        </tr>
      </table>
    </td>
  </tr>
  <tr>
    <td style="padding:20px 32px 8px;">
      <p style="margin:0 0 18px;font-size:14px;color:#374151;line-height:1.7;">
        Your organization has been formally verified and is now active within the TrustJet ecosystem. To begin managing your flight operations and client mandates, please complete the final stages of your profile configuration.
      </p>
      <p style="margin:0 0 8px;font-size:14px;color:#374151;line-height:1.7;">The final steps include:</p>
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin:0 0 22px;">
        <tr>
          <td style="padding:2px 0 2px 12px;font-size:14px;color:#374151;line-height:1.7;">
            &#8226;&nbsp;&nbsp;<strong>Team Integration:</strong> Invite your colleagues to the workspace.
          </td>
        </tr>
        <tr>
          <td style="padding:2px 0 2px 12px;font-size:14px;color:#374151;line-height:1.7;">
            &#8226;&nbsp;&nbsp;<strong>Financial Configuration:</strong> Securely link your banking details for seamless transactions.
          </td>
        </tr>
      </table>
    </td>
  </tr>
  <tr>
    <td style="padding:0 32px 32px;">
      <!-- CTA -->
      <table role="presentation" cellpadding="0" cellspacing="0">
        <tr>
          <td style="background-color:#22315e;border-radius:24px;">
            <a href="${LOGIN_URL}" target="_blank" style="display:inline-block;padding:12px 28px;font-size:14px;color:#FFFFFF;text-decoration:none;font-weight:500;font-family:'Rethink Sans',sans-serif;">
              Finalize Account Setup
            </a>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table>

${spacer(24)}

${emailFooter({ organization })}

${emailDocClose()}`;
};
